import { takeLatest, call, put, select, takeEvery } from 'redux-saga/effects';
import getBuyItAgainProductsApi, {
  getBuyItAgainProductsBatches,
  processProductData,
  getTotalProductsCount,
  getProductDetails,
  getSortedProducts,
  setDataInStorage,
  getBuyItAgainProductsGraphApi,
  getBuyItAgainProductsBatchesGraphQl,
} from '@tcp/core/src/services/abstractors/SFCC/BuyItAgain';
import endpoints from '@tcp/core/src/services/endpoints';
import logger from '@tcp/core/src/utils/loggerInstance';
import {
  buyItAgainProductsSuccess,
  setBiaIsLoadingMore,
  setBiaPageProducts,
  setBiaTotalProductsCount,
  setInitialProductsLoaded,
  buyItAgainFailure,
  resetBIA,
  toggleBIALoader,
  setBiaInitialApiActualProductCount,
  setBiaResponse,
} from './BuyItAgain.actions';

import BUY_IT_AGAIN_CONSTANTS, { BIA_SUFFIX } from './BuyItAgain.constants';
import {
  getBIABagpageConfigData,
  getIsBIAEnhancementEnabled,
} from '../../../../../reduxStore/selectors/session.selectors';
import { setBIAAvailableProduct } from '../../../CnC/BuyItAgainBagPage/container/BuyItAgainBagPage.actions';

const isFromBIA = true;
const callRetries = 3;
const isInitialProductsLoaded = (buyItAgainProducts) => {
  return buyItAgainProducts && buyItAgainProducts.initialProductsLoaded;
};

function deleteRootQueryProperty(propertyKey) {
  const localStorageData = window.localStorage;
  const cacheString = localStorageData.getItem('apollo-cache-persist');

  if (cacheString) {
    const parsedCache = JSON.parse(cacheString);

    // Check if parsedCache and ROOT_QUERY are not null, and the specific property exists
    if (parsedCache && parsedCache.ROOT_QUERY && parsedCache.ROOT_QUERY[propertyKey]) {
      delete parsedCache.ROOT_QUERY[propertyKey];
      localStorageData.setItem('apollo-cache-persist', JSON.stringify(parsedCache));
    }
  }
}

function handleLocalStorageAfterLogout(isBiaEnhancmentEnabled) {
  if (window && window.localStorage) {
    if (isBiaEnhancmentEnabled) {
      const localStorageData = window.localStorage;
      const parsedResponse = JSON.parse(localStorageData.GRAPHQL_CACHED_RESPONSE);

      deleteRootQueryProperty('getUserPurchaseHistory');

      Object.keys(parsedResponse).forEach((key) => {
        if (key.includes(BIA_SUFFIX)) {
          delete parsedResponse[key];
        }
      });
      localStorageData.GRAPHQL_CACHED_RESPONSE = JSON.stringify(parsedResponse);
    }

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < window.localStorage.length; i++) {
      if (window.localStorage.key(i).includes(BIA_SUFFIX)) {
        window.localStorage.removeItem(window.localStorage.key(i));
        i -= 1;
      }
    }
  }
}
const formatResponse = ({ products, response, fromBagPage }) => {
  return products?.map((item) => {
    const prodId = fromBagPage ? item?.productid : item?.productInfo?.generalProductId;
    const match = response?.find((product) => product?.productId === prodId);
    if (match) {
      return {
        ...item,
        lastPurchasedSize: match?.items[0]?.size,
      };
    }
    return item;
  });
};

export function* fetchBuyItAgainProducts({ payload }) {
  try {
    let productDetail = null;
    const { cacheTTL, sortingOrder, categoryCode, dateOnlySorting, noOfProducts } = payload;
    const fetchAllRecords = true;
    const isBiaEnhancmentEnabled = yield select(getIsBIAEnhancementEnabled);

    let productIds = [];
    let userPurchaseHistory = [];
    if (isBiaEnhancmentEnabled) {
      const graphQLpayload = {
        cacheTTL,
        sortingOrder: null,
        dateOnlySorting: true,
        categoryCode: null,
        dateSortingOrder: null,
        fetchAllRecords,
      };
      const response = yield call(getBuyItAgainProductsGraphApi, graphQLpayload);
      userPurchaseHistory = response;
      yield put(setBiaResponse(userPurchaseHistory));
      productIds = response?.map((item) => item.productId);
      yield put(setBiaResponse(userPurchaseHistory));
    } else {
      const cachedBuyItAgainProductsData = yield call(
        getBuyItAgainProductsApi,
        cacheTTL,
        sortingOrder,
        dateOnlySorting,
        categoryCode,
        fetchAllRecords
      );
      productIds = cachedBuyItAgainProductsData?.buyItAgainProducts;
    }
    if (productIds) {
      productDetail = yield call(
        getProductDetails,
        productIds,
        endpoints.getProductsBySearchTerm,
        productIds?.length,
        false,
        isFromBIA
      );
    }
    let sortedProducts = [];
    sortedProducts = getSortedProducts(productIds, productDetail);
    if (isBiaEnhancmentEnabled) {
      sortedProducts = formatResponse({
        products: sortedProducts,
        response: userPurchaseHistory,
        fromBagPage: true,
      });
    }
    const buyItAgainProducts = sortedProducts.slice(0, noOfProducts);
    const productIdsCount = productIds?.length;
    const storageKey = `ELIGIBLE_${BIA_SUFFIX}`;
    const isCartBIAEligible = buyItAgainProducts?.length > 0 || false;
    setDataInStorage(storageKey, isCartBIAEligible);

    yield put(
      buyItAgainProductsSuccess({
        categoryCode,
        buyItAgainProducts,
      })
    );
    const biaBagPageConfig = yield select(getBIABagpageConfigData);
    if (categoryCode === biaBagPageConfig?.categoryCode) {
      yield put(setBiaInitialApiActualProductCount(productIdsCount));
    }
  } catch (error) {
    yield put(buyItAgainFailure());
    logger.error({
      error,
      extraData: {
        component: 'Buy It Again Saga - fetchBuyItAgainProducts',
        payloadRecieved: payload,
      },
    });
  }
}

export function* fetchBiaPageProducts({ payload = {} }) {
  try {
    yield put(toggleBIALoader(true));
    let pageProducts = [];
    const { cacheTTL } = payload;
    const fetchAllRecords = true;
    const state = yield select();
    const isBiaEnhancmentEnabled = yield select(getIsBIAEnhancementEnabled);
    const initialProductsLoaded = isInitialProductsLoaded(state.buyItAgain);
    let productIds = [];
    let userPurchaseHistory = [];
    if (isBiaEnhancmentEnabled) {
      const response = yield call(
        getBuyItAgainProductsBatchesGraphQl,
        initialProductsLoaded,
        cacheTTL,
        fetchAllRecords
      );
      const { productIdArray, resp } = response;
      userPurchaseHistory = resp;
      productIds = productIdArray;
    } else {
      productIds = yield call(
        getBuyItAgainProductsBatches,
        initialProductsLoaded,
        cacheTTL,
        fetchAllRecords
      );
    }

    const totalProducts = yield call(getTotalProductsCount, isBiaEnhancmentEnabled);
    if (isBiaEnhancmentEnabled || initialProductsLoaded < totalProducts) {
      if (productIds?.length > 0) {
        const productList = yield call(
          getProductDetails,
          productIds,
          endpoints.getProductsBySearchTerm,
          undefined,
          false,
          isFromBIA
        );
        pageProducts = yield call(processProductData, productList);
      }
      let sortedProducts = [];
      sortedProducts = getSortedProducts(productIds, pageProducts);
      if (isBiaEnhancmentEnabled) {
        sortedProducts = formatResponse({
          products: sortedProducts,
          response: userPurchaseHistory,
        });
      }
      yield put(setBiaPageProducts(sortedProducts));
      yield put(setInitialProductsLoaded(productIds?.length));
      yield put(setBiaTotalProductsCount(totalProducts));
    }
    yield put(toggleBIALoader(false));
  } catch (error) {
    yield put(toggleBIALoader(false));
    logger.error({
      error,
      extraData: {
        component: 'Buy It Again Saga - fetchBiaPageProducts',
        payloadRecieved: payload,
      },
    });
  }
}

export function* getMoreBuyItAgainProducts({ payload = {} }) {
  try {
    let count = 0;
    let pageProducts = [];
    let userPurchaseHistory = [];
    const { cacheTTL } = payload;
    const fetchAllRecords = true;
    const state = yield select();
    const initialProductsLoaded = isInitialProductsLoaded(state.buyItAgain);
    const isBiaEnhancmentEnabled = yield select(getIsBIAEnhancementEnabled);
    const totalProductsCount = state?.buyItAgain?.totalProductsCount;
    if (initialProductsLoaded === totalProductsCount) {
      return;
    }
    if (count < callRetries) {
      yield put(toggleBIALoader(true));
      yield put(setBiaIsLoadingMore(true));
      let productIds = [];
      if (isBiaEnhancmentEnabled) {
        const response = yield call(
          getBuyItAgainProductsBatchesGraphQl,
          initialProductsLoaded,
          cacheTTL,
          fetchAllRecords
        );
        const { productIdArray, resp } = response;
        userPurchaseHistory = resp;
        productIds = productIdArray;
      } else {
        productIds = yield call(
          getBuyItAgainProductsBatches,
          initialProductsLoaded,
          cacheTTL,
          fetchAllRecords
        );
      }
      if (productIds?.length > 0) {
        const productList = yield call(
          getProductDetails,
          productIds,
          endpoints.getProductsBySearchTerm,
          undefined,
          false,
          isFromBIA
        );
        pageProducts = yield call(processProductData, productList);
      }
      let sortedProducts = [];
      sortedProducts = getSortedProducts(productIds, pageProducts);
      if (isBiaEnhancmentEnabled) {
        sortedProducts = formatResponse({
          products: sortedProducts,
          response: userPurchaseHistory,
        });
      }
      yield put(setBiaPageProducts(sortedProducts));
      yield put(setInitialProductsLoaded(initialProductsLoaded + productIds?.length));
      yield put(setBiaIsLoadingMore(false));
      yield put(toggleBIALoader(false));
      count += 1;
    }
  } catch (error) {
    yield put(toggleBIALoader(false));
    logger.error({
      error,
      extraData: {
        component: 'Buy It Again Saga - getMoreBuyItAgainProducts',
        payloadRecieved: payload,
      },
    });
  }
}

export function* clearBiaCache() {
  const isBiaEnhancmentEnabled = yield select(getIsBIAEnhancementEnabled);
  yield call(handleLocalStorageAfterLogout, isBiaEnhancmentEnabled);
  yield put(resetBIA());
  yield put(setBIAAvailableProduct(false));
}

function* BuyItAgainSaga() {
  yield takeEvery(BUY_IT_AGAIN_CONSTANTS.BUY_IT_AGAIN_PRODUCTS_START, fetchBuyItAgainProducts);
  yield takeLatest(BUY_IT_AGAIN_CONSTANTS.GET_MORE_BIA_PRODUCTS, getMoreBuyItAgainProducts);
  yield takeEvery(BUY_IT_AGAIN_CONSTANTS.FETCH_BIA_PAGE_PRODUCTS, fetchBiaPageProducts);
  yield takeLatest(BUY_IT_AGAIN_CONSTANTS.CLEAR_BIA_CACHE, clearBiaCache);
}

export default BuyItAgainSaga;
